import { useEffect, useMemo, useRef, useState } from 'react';

// Utils
import { formatDayLabel, getTodayYyyymmddLocal } from '../utils/formatDate';
import {
    ceilToHalfHour,
    clamp,
    floorToHalfHour,
    hhmmToMinutes,
    minutesToHHMM,
    toHHMM
} from '../utils/formatTime';
import { toCategoryKey } from '../utils/program/category';
import { layoutSideBySide } from '../utils/program/layout';
import {
    getDefaultViewModeByViewport,
    readStoredViewMode,
    storeViewMode
} from '../utils/program/storage';

export function useProgram() {
    const [events, setEvents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null); // YYYYMMDD
    const [selectedCategory, setSelectedCategory] = useState('trening'); // trening|konferanse|fellesprogram

    const [viewMode, setViewMode] = useState(() => {
        const stored = typeof window !== 'undefined' ? readStoredViewMode() : null;
        return stored || getDefaultViewModeByViewport();
    });

    const [modalEvent, setModalEvent] = useState(null);
    const [isModalClosing, setIsModalClosing] = useState(false);

    const [isDayPickerOpen, setIsDayPickerOpen] = useState(false);
    const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false);

    const dayLabelBtnRef = useRef(null);
    const dropdownRef = useRef(null);

    // Fetch + adapt
    useEffect(() => {
        fetch('/api/program/program.php?action=liste')
            .then(res => res.json())
            .then(json => {
                if (!json?.success) {
                    throw new Error(json?.error_message || 'Kunne ikke hente program');
                }

                const program = json?.data?.program || [];

                const adapted = program
                    .map(ev => {
                        const startIso = ev.fratid;
                        const endIso = ev.tiltid;

                        const startTime = toHHMM(startIso);
                        const endTime = toHHMM(endIso);

                        const startMs = startIso ? new Date(startIso).getTime() : 0;
                        const endMs = endIso ? new Date(endIso).getTime() : 0;

                        const durationMinutes =
                            startMs && endMs
                                ? Math.max(0, Math.round((endMs - startMs) / 60000))
                                : 0;

                        const override = ev.fullbredde === 'J';

                        return {
                            ...ev,
                            id: ev.akt_id,
                            title: ev.tittel,
                            dateKey: ev.dato,
                            categoryKey: toCategoryKey(ev.type_navn),
                            startIso,
                            endIso,
                            startTime,
                            endTime,
                            startMs,
                            endMs,
                            durationMinutes,
                            location: ev.sted || '',
                            description: ev.besk || '',
                            override
                        };
                    })
                    .filter(ev => ev.startTime && ev.endTime && ev.dateKey);

                setEvents(adapted);

                const dates = [...new Set(adapted.map(e => e.dateKey))]
                    .filter(Boolean)
                    .sort((a, b) => a - b);

                const todayKey = getTodayYyyymmddLocal();
                const initialDate = dates.includes(todayKey) ? todayKey : dates[0] || null;
                setSelectedDate(initialDate);

                const initialCats = adapted
                    .filter(e => e.dateKey === initialDate)
                    .map(e => e.categoryKey);

                const set = new Set(initialCats);

                const treningEnabled = set.has('trening');
                const konferanseEnabled = set.has('konferanse');

                setSelectedCategory(
                    treningEnabled ? 'trening' : konferanseEnabled ? 'konferanse' : 'trening'
                );
            })
            .catch(console.error);
    }, []);

    // Auto-switch viewMode on breakpoint change only if user hasn't stored choice
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const stored = readStoredViewMode();
        if (stored) return;

        const mql = window.matchMedia('(min-width: 1024px)');
        const onChange = () => setViewMode(mql.matches ? 'timeline' : 'list');

        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else mql.addListener(onChange);

        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else mql.removeListener(onChange);
        };
    }, []);

    const uniqueDates = useMemo(() => {
        const unique = [...new Set(events.map(e => e.dateKey))].filter(Boolean);
        return unique.sort((a, b) => a - b);
    }, [events]);

    const selectedDateIndex = useMemo(() => {
        if (!selectedDate) return -1;
        return uniqueDates.indexOf(selectedDate);
    }, [uniqueDates, selectedDate]);

    const selectedDayLabel = useMemo(() => {
        return selectedDate ? formatDayLabel(selectedDate) : '';
    }, [selectedDate]);

    const categoryAvailability = useMemo(() => {
        const dayEvents = events.filter(e => e.dateKey === selectedDate);

        const hasFelles = dayEvents.some(e => e.categoryKey === 'fellesprogram');
        const hasTrening = dayEvents.some(e => e.categoryKey === 'trening');
        const hasKonferanse = dayEvents.some(e => e.categoryKey === 'konferanse');

        return {
            hasFelles,
            hasTrening,
            hasKonferanse,
            treningEnabled: hasTrening,
            konferanseEnabled: hasKonferanse
        };
    }, [events, selectedDate]);

    // Keep selectedCategory valid for the chosen day and force default when both are available.
    useEffect(() => {
        if (!selectedDate) return;

        const { treningEnabled, konferanseEnabled } = categoryAvailability;

        // 1) Begge finnes -> default trening
        if (treningEnabled && konferanseEnabled) {
            if (selectedCategory !== 'trening') setSelectedCategory('trening');
            return;
        }

        // 2) Bare trening -> trening
        if (treningEnabled && !konferanseEnabled) {
            if (selectedCategory !== 'trening') setSelectedCategory('trening');
            return;
        }

        // 3) Bare konferanse -> konferanse
        if (!treningEnabled && konferanseEnabled) {
            if (selectedCategory !== 'konferanse') setSelectedCategory('konferanse');
            return;
        }

        // 4) Bare felles (eller ingenting) -> trening (men begge disabled i UI)
        if (selectedCategory !== 'trening') setSelectedCategory('trening');
    }, [selectedDate, categoryAvailability]);

    const filteredEvents = useMemo(() => {
        return events
            .filter(e => {
                if (e.dateKey !== selectedDate) return false;
                if (e.categoryKey === 'fellesprogram') return true;
                return e.categoryKey === selectedCategory;
            })
            .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs || a.id - b.id);
    }, [events, selectedDate, selectedCategory]);

    // const timelineEvents = useMemo(() => layoutSideBySide(filteredEvents), [filteredEvents]);
    const timelineEvents = useMemo(() => {
        const full = filteredEvents.filter(e => e.override);
        const normal = filteredEvents.filter(e => !e.override);

        const laidOut = layoutSideBySide(normal);

        // full-width har egne style-regler i render
        return [...laidOut, ...full];
    }, [filteredEvents]);

    const listEvents = useMemo(() => filteredEvents, [filteredEvents]);

    const timeWindow = useMemo(() => {
        if (!filteredEvents.length) {
            const start = 7 * 60;
            const end = 22 * 60;
            return { startMin: start, endMin: end, baseMin: start };
        }

        const starts = [];
        const ends = [];

        for (const e of filteredEvents) {
            const s = hhmmToMinutes(e.startTime);
            let en = hhmmToMinutes(e.endTime);

            if (typeof s !== 'number' || typeof en !== 'number') continue;
            if (en <= s) en += 24 * 60;

            starts.push(s);
            ends.push(en);
        }

        if (!starts.length || !ends.length) {
            const start = 7 * 60;
            const end = 22 * 60;
            return { startMin: start, endMin: end, baseMin: start };
        }

        const minStart = Math.min(...starts);
        const maxEnd = Math.max(...ends);

        let startMin = floorToHalfHour(minStart);
        let endMin = ceilToHalfHour(maxEnd + 30);

        startMin = clamp(startMin, 0, 24 * 60);
        endMin = clamp(endMin, 0, 48 * 60);

        const minWindow = 3 * 60;
        if (endMin - startMin < minWindow) endMin = clamp(startMin + minWindow, 0, 48 * 60);

        return { startMin, endMin, baseMin: startMin };
    }, [filteredEvents]);

    const timeSlots = useMemo(() => {
        const s = [];
        for (let m = timeWindow.startMin; m <= timeWindow.endMin; m += 30) {
            s.push(minutesToHHMM(m));
        }
        return s;
    }, [timeWindow]);

    const SLOT_PX = 48;
    const stageHeightPx = useMemo(() => timeSlots.length * SLOT_PX, [timeSlots.length]);

    // Handlers
    const handleOpenModal = ev => setModalEvent(ev);

    const handleCloseModal = () => {
        setIsModalClosing(true);
        setTimeout(() => {
            setModalEvent(null);
            setIsModalClosing(false);
        }, 200);
    };

    const setViewModeAndPersist = mode => {
        setViewMode(mode);
        storeViewMode(mode);
    };

    const goPrevDay = () => {
        if (selectedDateIndex <= 0) return;
        setSelectedDate(uniqueDates[selectedDateIndex - 1]);
    };

    const goNextDay = () => {
        if (selectedDateIndex < 0 || selectedDateIndex >= uniqueDates.length - 1) return;
        setSelectedDate(uniqueDates[selectedDateIndex + 1]);
    };

    const openDayPicker = () => {
        if (typeof window === 'undefined') return;

        if (!window.matchMedia('(min-width: 1024px)').matches) {
            setIsDayPickerOpen(true);
            return;
        }

        setIsDayDropdownOpen(v => !v);
    };

    // Close dropdown on outside click / Escape
    useEffect(() => {
        if (!isDayDropdownOpen) return;

        const onKeyDown = e => {
            if (e.key === 'Escape') setIsDayDropdownOpen(false);
        };

        const onPointerDown = e => {
            const t = e.target;
            if (!dropdownRef.current) return;
            if (dropdownRef.current.contains(t)) return;
            if (dayLabelBtnRef.current && dayLabelBtnRef.current.contains(t)) return;
            setIsDayDropdownOpen(false);
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerdown', onPointerDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [isDayDropdownOpen]);

    // Bottom sheet: Escape support + lock body scroll
    useEffect(() => {
        if (!isDayPickerOpen) return;

        const onKeyDown = e => {
            if (e.key === 'Escape') setIsDayPickerOpen(false);
        };

        document.addEventListener('keydown', onKeyDown);
        document.body.classList.add('no-scroll');

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.classList.remove('no-scroll');
        };
    }, [isDayPickerOpen]);

    const isReady = events.length > 0 && !!selectedDate && !!selectedCategory;

    return {
        // state
        events,
        selectedDate,
        selectedCategory,
        viewMode,
        modalEvent,
        isModalClosing,
        isDayPickerOpen,
        isDayDropdownOpen,

        // derived
        uniqueDates,
        selectedDateIndex,
        selectedDayLabel,
        categoryAvailability,
        listEvents,
        timelineEvents,
        timeWindow,
        timeSlots,
        stageHeightPx,
        isReady,

        // refs
        dayLabelBtnRef,
        dropdownRef,

        // setters (for components that need direct set)
        setSelectedDate,
        setSelectedCategory,
        setIsDayPickerOpen,
        setIsDayDropdownOpen,

        // handlers
        handleOpenModal,
        handleCloseModal,
        setViewModeAndPersist,
        goPrevDay,
        goNextDay,
        openDayPicker
    };
}
